import type { PipeFrictionIntermediates } from "..";
import { GRAVITY_FT_SEC } from "../../constants";
import type { PipeFrictionContext } from "../d";

export function getHagedornBrownFrictionProperties(
  context: PipeFrictionContext
): PipeFrictionIntermediates {
  const {
    fluidPropertiesContext: {
      fluidProperties: {
        liquidSuperficialVelocityFTSEC,
        mixSuperficialVelocityFTSEC,
        gasSuperficialVelocityFTSEC,
        liquidViscosityCP,
        liquidDensityLBMFT3,
        gasDensityLBMFT3,
        gasViscosityCP
      },
      flowRatesInSitu,
      fluidFrictionProperties: {
        noSlipLiquidHoldupLambdaCL,
        totalMass,
        liquidViscosityNumber,
        liquidInterfacialTension
      }
    },
    pipeDiameterIn,
    pipeLengthFt,
    epsilonD,
    griffithWallis,
    // gasVelocityNumber,
    // liquidVelocityNumber,
    referencePointPressurePSIA,
    pipeDiameterNumber
  } = context;
  // } = fluidPropertiesContext.fluidProperties;
  if (!flowRatesInSitu) throw new Error("flowRatesInSitu must be defined");
  const flowRegime = "N/A";
  const { liquidFlowRateBBLD } = flowRatesInSitu;

  const includeGriffithWallis = griffithWallis || false;
  // const tubingFlowCorrelation =
  //   correlations?.tubing_flow || Tubing_Flow_Options_Enum.BeggsBrill;

  // CN_L'
  // D192: =(0.0019+0.0322*D190-0.6642*D190^2+4.9951*D190^3)/(1-10.0147*D190+33.8696*D190^2+277.2817*D190^3)
  const liquidViscosityReferenceCoefficent =
    (0.0019 +
      0.0322 * liquidViscosityNumber -
      0.6642 * liquidViscosityNumber ** 2 +
      4.9951 * liquidViscosityNumber ** 3) /
    (1 -
      10.0147 * liquidViscosityNumber +
      33.8696 * liquidViscosityNumber ** 2 +
      277.2817 * liquidViscosityNumber ** 3);

  // CN_L
  // D194: =IF(D190>0.4,0.0115,IF(D190<0.002,0.0019,D192))
  let liquidViscosityNumberCoefficent = liquidViscosityReferenceCoefficent;
  if (liquidViscosityNumber > 0.04) {
    liquidViscosityNumberCoefficent = 0.0115;
  } else if (liquidViscosityNumber < 0.002) {
    liquidViscosityNumberCoefficent = 0.0019;
  }

  // N_LV
  // D196: =D150*((D63/(D67*D168))^(1/4))
  const liquidVelocityNumber =
    liquidSuperficialVelocityFTSEC *
    (liquidDensityLBMFT3 / (GRAVITY_FT_SEC * liquidInterfacialTension)) ** 0.25;

  // N_GV
  // D198: =D149*(D63/(D67*D168))^(1/4)
  const gasVelocityNumber =
    gasSuperficialVelocityFTSEC *
    (liquidDensityLBMFT3 / (GRAVITY_FT_SEC * liquidInterfacialTension)) ** 0.25;

  // Xh
  // D202: =((D196/(D198^0.575))*((D10/14.7)^0.1)*(D194/D200))
  const correlationGroupTheta =
    gasVelocityNumber === 0
      ? 0
      : (liquidVelocityNumber / gasVelocityNumber ** 0.575) *
        (referencePointPressurePSIA / 14.7) ** 0.1 *
        (liquidViscosityNumberCoefficent / pipeDiameterNumber);

  // HL/U
  // D204: =((0.0047+1123.32*D202+729489.64*D202^2)/(1+1097.1566*D202+722153.97*D202^2))^(1/2)
  const liquidHoldupRatio =
    correlationGroupTheta === 0
      ? 0
      : ((0.0047 +
          1123.32 * correlationGroupTheta +
          729489.64 * correlationGroupTheta ** 2) /
          (1 +
            1097.1566 * correlationGroupTheta +
            722153.97 * correlationGroupTheta ** 2)) **
        (1 / 2);

  // Xps
  // D206: =(D198*(D196^0.38))/(D200^2.14)
  const secondaryCorrelationGroupTheta =
    (gasVelocityNumber * liquidVelocityNumber ** 0.38) /
    pipeDiameterNumber ** 2.14;

  // U
  // D208: =IF(D206>0.055,G210,IF(D206>0.025,G209,G208))
  let secondaryCorrelation = 0;
  if (secondaryCorrelationGroupTheta > 0.055) {
    secondaryCorrelation = 2.5714 * secondaryCorrelationGroupTheta + 1.5962;
  } else if (secondaryCorrelationGroupTheta > 0.025) {
    secondaryCorrelation =
      -533.33 * secondaryCorrelationGroupTheta ** 2 +
      58.524 * secondaryCorrelationGroupTheta +
      0.1171;
  } else {
    secondaryCorrelation =
      27170 * secondaryCorrelationGroupTheta ** 3 -
      317.52 * secondaryCorrelationGroupTheta ** 2 +
      0.5472 * secondaryCorrelationGroupTheta +
      0.9999;
  }

  // H_L
  // D210: =IF(D179="Griffith",D184,IFERROR((D204/D208)*D208,D153))
  let liquidHoldup = noSlipLiquidHoldupLambdaCL;
  if (includeGriffithWallis) {
    liquidHoldup =
      1 -
      0.5 *
        (1 +
          mixSuperficialVelocityFTSEC / 0.24384 -
          Math.sqrt(
            (1 + mixSuperficialVelocityFTSEC / 0.24384) ** 2 -
              (4 * gasSuperficialVelocityFTSEC) / 0.24384
          ));
  } else if (secondaryCorrelation !== 0 && liquidHoldupRatio !== 0) {
    liquidHoldup =
      (liquidHoldupRatio / secondaryCorrelation) * secondaryCorrelation;
  }

  // P_m
  // D212: =D63*D210+D62*(1-D210)
  const mixtureDensityLBFT3 =
    liquidDensityLBMFT3 * liquidHoldup + gasDensityLBMFT3 * (1 - liquidHoldup);

  // Re
  // D214: =(2.2*10^(-2))*((D138*D188)/((D132/12)*D130^D210*D129^(1-D210)))
  const reynoldsTwoPhaseNumber =
    2.2 *
    10 ** -2 *
    ((liquidFlowRateBBLD * totalMass) /
      ((pipeDiameterIn / 12) *
        liquidViscosityCP ** liquidHoldup *
        gasViscosityCP ** (1 - liquidHoldup)));

  // V_L
  // D216: =D150/D210
  const inSituLiquidVelocityFTSEC =
    liquidSuperficialVelocityFTSEC / liquidHoldup;

  // Chen f_no slip
  // D220: =(1/(-2*LOG10(D218/3.7065-(5.0452/D214)*LOG10(((D218^1.1098)/2.8257)+(5.8506/(D214^0.8981))))))^2
  const chenHagedorn = Math.pow(
    1 /
      (-2 *
        Math.log10(
          epsilonD / 3.7065 -
            (5.0452 / reynoldsTwoPhaseNumber) *
              Math.log10(
                epsilonD ** 1.1098 / 2.8257 +
                  5.8506 / reynoldsTwoPhaseNumber ** 0.8981
              )
        )),
    2
  );

  // p_no_slip
  // D222: =D153*D63+(1-D153)*D62
  const noSlipDensityLBFT3 =
    liquidDensityLBMFT3 * noSlipLiquidHoldupLambdaCL +
    (1 - noSlipLiquidHoldupLambdaCL) * gasDensityLBMFT3;

  // Friction Pressure Gradient dpf/dz
  // D224: =((2*D220*D151^2)/D132)*(D222/D212)
  const frictionPressureGradient =
    ((2 * chenHagedorn * mixSuperficialVelocityFTSEC ** 2) / pipeDiameterIn) *
    (noSlipDensityLBFT3 / mixtureDensityLBFT3);

  // dPg/dz
  // D226: =(D212/144)*D3*SIN(D8)
  // HYDROSTATIC PRESSURE NEEDS MOVED SOMEWHERE OUT OF HERE
  // const hydrostaticPressurePSIA = (mixtureDensityLBFT3 / 144) * hydrostaticSum;

  // D228: =((2*D220*((D222^2)/D212)*D151^2)/(144*D67*D132))*D3
  const frictionPressurePSIA =
    ((2 *
      chenHagedorn *
      (noSlipDensityLBFT3 ** 2 / mixtureDensityLBFT3) *
      mixSuperficialVelocityFTSEC ** 2) /
      (144 * GRAVITY_FT_SEC * pipeDiameterIn)) *
    pipeLengthFt;

  const frictionProperties: PipeFrictionIntermediates = {
    frictionPressurePSIA,
    flowRegime,
    totalMass,
    mixtureDensityLBFT3,
    reynoldsTwoPhaseNumber,
    inSituLiquidVelocityFTSEC,
    chenHagedorn,
    noSlipDensityLBFT3,
    frictionPressureGradient,
    liquidViscosityNumber,
    liquidViscosityReferenceCoefficent,
    liquidViscosityNumberCoefficent,
    liquidVelocityNumber,
    gasVelocityNumber,
    correlationGroupTheta,
    liquidHoldupRatio,
    secondaryCorrelationGroupTheta,
    secondaryCorrelation,
    includeGriffithWallis,
    liquidHoldup,
    noSlipLiquidHoldupLambdaCL
  };

  return frictionProperties;
}
